import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SEO_DATA } from '../../core/seo/seo-data';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router
  ) {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        const currentUrl = event.urlAfterRedirects;
        const routeKey = this.getRouteKey(currentUrl);
        const seo = SEO_DATA[routeKey];

        if (seo) {
          this.setMetaData(seo.title, seo.description, seo.canonicalUrl, seo.imageUrl);
          const jsonLdData = seo.structuredData || this.generateJsonLd(routeKey);
          if (jsonLdData) {
            this.injectJsonLd(jsonLdData);
          }
          this.setRobotsMeta('index, follow');
        } else {
          this.setMetaData('arq-it', '', `${window.location.origin}${currentUrl}`);
          this.setRobotsMeta('noindex, nofollow');
        }
      });
  }

  private getRouteKey(url: string): string {
    const clean = url.split('?')[0].replace(/^\/|\/$/g, '');
    return clean === '' ? 'home' : clean;
  }

  setMetaData(
    title: string,
    description: string,
    canonicalUrl?: string,
    imageUrl?: string
  ): void {
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });

    if (canonicalUrl) {
      this.setCanonicalURL(canonicalUrl);
      this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    }

    if (imageUrl) {
      this.metaService.updateTag({ property: 'og:image', content: imageUrl });
    }
  }

  private setCanonicalURL(url: string): void {
    const existingLink = document.querySelector("link[rel='canonical']");
    if (existingLink) {
      existingLink.setAttribute('href', url);
    } else {
      const link: HTMLLinkElement = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      document.head.appendChild(link);
    }
  }

  setRobotsMeta(content: string): void {
    const existingMeta = document.querySelector('meta[name="robots"]');
    if (existingMeta) {
      existingMeta.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  }

  generateJsonLd(pageKey: string): any {
    const seo = SEO_DATA[pageKey];
    let type = 'WebPage';
    if (pageKey === 'about') type = 'AboutPage';
    else if (pageKey === 'contact') type = 'ContactPage';

    return {
      "@context": "https://schema.org",
      "@type": type,
      "name": seo.title,
      "description": seo.description,
      "url": seo.canonicalUrl,
      "publisher": {
        "@type": "Organization",
        "name": "arq-it",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.dev.arq-it.cl/assets/svg/logo.svg"
        }
      }
    };
  }

  injectJsonLd(data: any): void {
    const scriptId = 'json-ld-tag';
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;

    
    const content = Array.isArray(data) ? JSON.stringify(data) : JSON.stringify([data]);
    script.text = content;

    document.head.appendChild(script);
  }
}
